import { hbs } from 'ember-cli-htmlbars';
const __COLOCATED_TEMPLATE__ = hbs("{{! @glint-nocheck: not typesafe yet }}\n{{! template-lint-disable no-invalid-interactive }}\n<li class=\"cNO--panelBody__li\" {{on \"click\" this.onClick}}>\n\n  <div class=\"cNO--item\">\n    <div class=\"cNO--itemMeta\">\n      <div class=\"cNO--itemMeta__userInitial\">\n        {{this.usernameInitial}}\n      </div>\n    </div>\n\n    <div class=\"cNO--itemContent\">\n      <div class=\"cNO--itemContent__header\">\n        <span class=\"cNO--itemContent__title\">{{this.notificationTitle}}</span>\n        {{#unless @notification.read_at}}\n          <div class=\"cNO--itemContent__badge\"></div>\n        {{/unless}}\n      </div>\n\n      <div class=\"cNO--itemContent__body\">\n        {{yield}}\n      </div>\n\n      <div class=\"cNO--itemContent__footer\">\n        {{this.formattedNotificationTime}}\n\n        {{#if @actionButton}}\n          <Shared::Ui::Button @size=\"small\" @text={{@actionButton}} @variant=\"default\" />\n        {{/if}}\n      </div>\n    </div>\n  </div>\n\n</li>", {"contents":"{{! @glint-nocheck: not typesafe yet }}\n{{! template-lint-disable no-invalid-interactive }}\n<li class=\"cNO--panelBody__li\" {{on \"click\" this.onClick}}>\n\n  <div class=\"cNO--item\">\n    <div class=\"cNO--itemMeta\">\n      <div class=\"cNO--itemMeta__userInitial\">\n        {{this.usernameInitial}}\n      </div>\n    </div>\n\n    <div class=\"cNO--itemContent\">\n      <div class=\"cNO--itemContent__header\">\n        <span class=\"cNO--itemContent__title\">{{this.notificationTitle}}</span>\n        {{#unless @notification.read_at}}\n          <div class=\"cNO--itemContent__badge\"></div>\n        {{/unless}}\n      </div>\n\n      <div class=\"cNO--itemContent__body\">\n        {{yield}}\n      </div>\n\n      <div class=\"cNO--itemContent__footer\">\n        {{this.formattedNotificationTime}}\n\n        {{#if @actionButton}}\n          <Shared::Ui::Button @size=\"small\" @text={{@actionButton}} @variant=\"default\" />\n        {{/if}}\n      </div>\n    </div>\n  </div>\n\n</li>","moduleName":"later/components/nav/notification/wrapper.hbs","parseOptions":{"srcName":"later/components/nav/notification/wrapper.hbs"}});
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { SafeString } from '@ember/template/-private/handlebars';
import type StoreService from '@ember-data/store';
import type { FeedItem } from '@knocklabs/client';
import type { GenericData } from '@knocklabs/types';
import type SocialProfileModel from 'later/models/social-profile';
import type LocaleService from 'later/services/locale';
import type { Maybe } from 'shared/types';

interface NotificationWrapperComponentSignature {
  Element: HTMLDivElement;
  Args: {
    onClick: () => void;
    onInit: () => Promise<void>;
    notification: FeedItem<GenericData>;
    notificationTitle: SafeString | string;
  };
  Blocks: {
    default: [];
  };
}

export default class NotificationWrapperComponent extends Component<NotificationWrapperComponentSignature> {
  @service declare locale: LocaleService;
  @service declare store: StoreService;

  constructor(owner: unknown, args: NotificationWrapperComponentSignature['Args']) {
    super(owner, args);
    if (typeof this.args.onInit == 'function') {
      this.args.onInit();
    }
  }

  get notification(): FeedItem<GenericData> {
    return this.args.notification;
  }

  get notificationData(): GenericData | null {
    return this.notification.data;
  }

  get usernameInitial(): string {
    return this.notificationData?.initiator?.[0] || '?';
  }

  get notificationTitle(): SafeString | string {
    return this.args.notificationTitle || '';
  }

  get socialProfile(): Maybe<SocialProfileModel> {
    if (this.notificationData?.social_profile_id) {
      return this.store.peekRecord('social-profile', this.notificationData.social_profile_id);
    }

    return undefined;
  }

  get formattedNotificationTime(): string {
    const date = new Date(this.notification.inserted_at);

    return this.#formatNotificationTime(date.getTime());
  }

  @action
  onClick(): void {
    if (typeof this.args.onClick === 'function') {
      this.args.onClick();
    }
  }

  #formatDateTime(): Intl.DateTimeFormat {
    return new Intl.DateTimeFormat(this.locale.primaryLocale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    });
  }

  #formatTodayDateTime(): Intl.DateTimeFormat {
    return new Intl.DateTimeFormat(this.locale.primaryLocale, {
      month: 'short',
      day: '2-digit'
    });
  }

  #isCreatedTimeToday(createdTime: number): boolean {
    return this.#formatTodayDateTime().format(createdTime) === this.#formatTodayDateTime().format(Date.now());
  }

  #formatNotificationTime(createdTime: number): string {
    if (this.#isCreatedTimeToday(createdTime)) {
      const relativeTime = this.locale.formatRelativeTime(createdTime) || this.#formatDateTime().format(createdTime);
      return relativeTime;
    }

    return this.#formatDateTime().format(createdTime);
  }
}
